import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { checkRedisHealth } from "@/utils/checkRedisHealth";
import { SignedInSessionResource } from "@clerk/types";
import { getUnexpiredToken } from "@/utils/utils";

export const codebaseSlice = createSlice({
  name: "codebase",
  initialState: {
    fileTree: [],
    fileTreeStatus: "idle",
    fileTreeRequestInProgress: false,
    fileToContents: {},
    allKnownFiles: new Set(),
    basePathToProject: null,

    // Dev server status
    devServerRequestStatus: "idle",
    devServerRequestInProgress: false,
    devServerStatus: null as {
      devServerConsoleConnected: boolean;
      isServerRunning: boolean;
      projectCloned: boolean;
      containerStatus: {
        found: boolean;
        running: boolean;
        configUpToDate: boolean;
        mountsUpToDate: boolean;
      } | null;
    } | null,

    unauthorized: null as boolean | null,
    deleted: null as boolean | null,
    githubAccess: null as boolean | null,
    gitlabAccess: null as boolean | null,
    bitbucketAccess: null as boolean | null,
    serverDown: false as boolean,
  },
  reducers: {
    setFileTreeRequestInProgress: (state, action) => {
      state.fileTreeRequestInProgress = action.payload;
    },
    setDevServerRequestInProgress: (state, action) => {
      state.devServerRequestInProgress = action.payload;
    },
    setGithubAccess: (state, action) => {
      state.githubAccess = action.payload;
    },
    setGitlabAccess: (state, action) => {
      state.gitlabAccess = action.payload;
    },
    setBitbucketAccess: (state, action) => {
      state.bitbucketAccess = action.payload;
    },
    resetCodebaseState: (state) => {
      state.fileTree = [];
      state.fileTreeStatus = "idle";
      state.fileTreeRequestInProgress = false;
      state.fileToContents = {};
      state.allKnownFiles = new Set();
      state.basePathToProject = null;

      state.devServerRequestStatus = "idle";
      state.devServerRequestInProgress = false;
      state.devServerStatus = null;

      state.unauthorized = null;
      state.githubAccess = null;
      state.serverDown = false;
    },
    setUnauthorized: (state, action: { payload: boolean }) => {
      state.unauthorized = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchFileTree.pending, (state, action) => {
        state.fileTreeStatus = "loading";
      })
      .addCase(fetchFileTree.fulfilled, (state, action) => {
        if (!action.payload?.fileTree) {
          return;
        }

        state.fileTreeStatus = "succeeded";
        state.fileTreeRequestInProgress = false;
        state.fileTree = action.payload.fileTree;

        const canvasId = action.payload.canvasId;

        const newKnownFiles = new Set();
        let newBasePathToProject = null;

        const fetchAllFiles = (file) => {
          newKnownFiles.add(file.fullPath);

          if (!newBasePathToProject) {
            // @ts-expect-error TS2322
            newBasePathToProject =
              file.fullPath.split(canvasId)?.[0] + `${canvasId}/`;
          }

          if (file.items) {
            file.items.forEach((item) => {
              fetchAllFiles(item);
            });
          }
        };

        state.fileTree?.forEach((file) => {
          fetchAllFiles(file);
        });

        state.allKnownFiles = newKnownFiles;
        state.basePathToProject = newBasePathToProject;
      })
      .addCase(fetchFileTree.rejected, (state, action) => {
        state.fileTreeStatus = "failed";
        state.fileTreeRequestInProgress = false;
      })

      .addCase(fetchDevServerStatus.pending, (state, action) => {
        state.devServerRequestStatus = "loading";
      })
      .addCase(fetchDevServerStatus.fulfilled, (state, action) => {
        if (action.payload === null) {
          return;
        }

        state.devServerRequestStatus = "succeeded";
        state.devServerRequestInProgress = false;
        state.devServerStatus = action.payload;
        state.serverDown = false;
      })
      .addCase(fetchDevServerStatus.rejected, (state, action) => {
        state.devServerRequestInProgress = false;
        state.devServerRequestStatus = "failed";

        // Do not update the dev server status, use the previous one

        const { payload } = action as any;

        if (payload?.confirmedRedisDown) {
          state.serverDown = true;
          state.deleted = false;
        } else if (payload?.code === 403 && !payload?.anyRedisCheckFailed) {
          state.serverDown = false;
          state.deleted = true;
        } else {
          state.serverDown = false;
          state.deleted = false;
        }
      });
  },
});

interface FetchFileTreeArgs {
  canvasId: string;
  authToken?: string | null | undefined;
  session: SignedInSessionResource;
}

export const fetchFileTree = createAsyncThunk(
  "codebase/fetchFileTree",
  async (data: FetchFileTreeArgs, thunkAPI: any) => {
    const { canvasId, session } = data;

    const token = await getUnexpiredToken(session);

    if (!canvasId || !token) {
      return null;
    }

    if (thunkAPI.getState().codebase.fileTreeRequestInProgress) {
      return null;
    }

    thunkAPI.dispatch(codebaseSlice.actions.setFileTreeRequestInProgress(true));

    const res = await axios.get(
      `${import.meta.env.VITE_BACKEND_BASE_URL}/canvases/${canvasId}/fileTree`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return {
      fileTree: res.data?.fileTree,
      canvasId,
    };
  },
);

interface FetchDevServerStatusArgs {
  canvasId: string;
  supabaseClient: any;
  session: SignedInSessionResource;
}

export const fetchDevServerStatus = createAsyncThunk(
  "codebase/fetchDevServerStatus",
  async (data: FetchDevServerStatusArgs, thunkAPI: any) => {
    const { canvasId, supabaseClient, session } = data;

    const token = await getUnexpiredToken(session);

    if (!canvasId || !token) {
      return;
    }

    if (thunkAPI.getState().codebase.devServerRequestInProgress) {
      return null;
    }

    thunkAPI.dispatch(
      codebaseSlice.actions.setDevServerRequestInProgress(true),
    );

    try {
      const res = await axios.get(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/devServer/devServerStatus`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return res.data;
    } catch (e) {
      // Check if the response exists and has a status
      const statusCode = e.response ? e.response.status : null;
      const message =
        e.response && e.response.data ? e.response.data.message : e.message;

      const { confirmedRedisDown, anyCheckFailed: anyRedisCheckFailed } =
        await checkRedisHealth(canvasId, supabaseClient);

      // Reject the thunk with a value that contains both the code and the message
      return thunkAPI.rejectWithValue({
        code: statusCode,
        message,
        confirmedRedisDown,
        anyRedisCheckFailed,
      });
    }
  },
);

// Action creators are generated for each case reducer function
export const {
  setGithubAccess,
  setGitlabAccess,
  setBitbucketAccess,
  resetCodebaseState,
  setUnauthorized,
} = codebaseSlice.actions;

export default codebaseSlice.reducer;
